import type {
  EstadoDeInscripcionDelPozo,
  FuenteDeAguaDelInforme,
  GuardarAguasInput,
  ObraDeAguaDelInforme,
  TipoDeFuenteDeAgua,
  TipoDeObraDeAgua,
} from "@/lib/informes";
import { textoDeDecimal } from "@/lib/montos";
import { numeroEscrito } from "@/lib/numeroEscrito";

/* ---------------------------------------------------------------------------
   La pestaña "Descriptor de aguas": traducción formulario ↔ API y el TOTAL de
   agua disponible del predio.

   VIVE FUERA DEL COMPONENTE POR EL TOTAL, que es lo único acá que puede estar
   mal sin que nada se vea roto: una suma equivocada se imprime como una cifra
   perfectamente formada, al lado de los números que la persona acaba de
   escribir. Dentro del árbol, un test solo podría ejercitarlo a través de
   `render` y no distinguiría un redondeo malo de uno bueno.
--------------------------------------------------------------------------- */

/**
 * Una fila del cuadro mientras se escribe.
 *
 * TODO ES STRING porque eso es lo que hay en un `<input>`: guardarlo como
 * número obligaría a decidir qué es un campo a medio teclear ("1,") y la
 * respuesta siempre sería borrar lo que la persona está escribiendo.
 *
 * `clave` NO ES EL `id` DEL SERVIDOR, y por eso existe: una fila recién
 * agregada todavía no tiene id, y usar el índice del arreglo haría que quitar
 * la primera le pasara su identidad a la segunda —React reusaría su estado y
 * el foco saltaría de campo—. Es la misma `clave` del cuadro de clases de
 * suelo.
 */
export interface FilaDeFuenteDeAgua extends IdentidadDeLaFila {
  clave: string;
  tipo: TipoDeFuenteDeAgua;
  rio_canal: string;
  acciones: string;
  litros_seg: string;
  equivalencia_ls_accion: string;
  estado_inscripcion: EstadoDeInscripcionDelPozo | "";
  validada: boolean;
}

export interface FilaDeObraDeAgua extends IdentidadDeLaFila {
  clave: string;
  tipo: TipoDeObraDeAgua;
  capacidad_descripcion: string;
  validada: boolean;
}

/**
 * Lo que une una fila del formulario con lo que el servidor ya tiene.
 *
 * `id` es el de la fila guardada, o `null` si todavía no existe. Viaja en el
 * PUT porque el servidor RECONCILIA por id: una fila con id se actualiza y
 * conserva los registros de terreno que cuelgan de ella; sin id se crea.
 *
 * `terreno_ref` es el uuid del registro de terreno que esta fila reemplaza en
 * pantalla y `terreno_ref_guardado` lo que el servidor tenía al cargar. Se
 * guardan los dos para mandar el enlace SOLO cuando cambió: ausente conserva el
 * que hay, y mandarlo siempre le daría al servidor una decisión que la persona
 * no tomó.
 */
export interface IdentidadDeLaFila {
  id: number | null;
  terreno_ref: string | null;
  terreno_ref_guardado: string | null;
}

export const SIN_IDENTIDAD: IdentidadDeLaFila = { id: null, terreno_ref: null, terreno_ref_guardado: null };

/**
 * Una fuente nueva arranca como SUPERFICIAL.
 *
 * No es un sorteo ni un vacío: el selector no admite "sin elegir" —el tipo es
 * lo que decide qué campos se dibujan, así que una fila sin tipo no se podría
 * ni mostrar— y el canal es el caso corriente de un predio agrícola chileno.
 */
export function fuenteDeAguaVacia(): FilaDeFuenteDeAgua {
  return {
    clave: crypto.randomUUID(),
    tipo: "superficial_canal",
    rio_canal: "",
    acciones: "",
    litros_seg: "",
    equivalencia_ls_accion: "",
    estado_inscripcion: "",
    validada: false,
    ...SIN_IDENTIDAD,
  };
}

export function obraDeAguaVacia(): FilaDeObraDeAgua {
  return {
    clave: crypto.randomUUID(),
    tipo: "tranque",
    capacidad_descripcion: "",
    validada: false,
    ...SIN_IDENTIDAD,
  };
}

/**
 * Carga el formulario con las fuentes ya guardadas.
 *
 * NO SE PIERDE LO QUE EL TIPO NO ADMITE aunque no se vea: el servidor ya lo
 * descartó al guardar, así que lo que llega en `null` es exactamente lo que no
 * corresponde. Traerlo igual deja que cambiar el tipo de ida y vuelta no
 * invente datos.
 */
export function filasDesdeFuentes(fuentes: FuenteDeAguaDelInforme[]): FilaDeFuenteDeAgua[] {
  return fuentes.map((fuente) => ({
    clave: `guardada-${fuente.id}`,
    tipo: fuente.tipo,
    rio_canal: fuente.rio_canal ?? "",
    acciones: textoDeDecimal(fuente.acciones),
    litros_seg: textoDeDecimal(fuente.litros_seg),
    equivalencia_ls_accion: textoDeDecimal(fuente.equivalencia_ls_accion),
    estado_inscripcion: fuente.estado_inscripcion ?? "",
    validada: fuente.validada,
    id: fuente.id,
    terreno_ref: fuente.terreno_ref ?? null,
    terreno_ref_guardado: fuente.terreno_ref ?? null,
  }));
}

export function filasDesdeObras(obras: ObraDeAguaDelInforme[]): FilaDeObraDeAgua[] {
  return obras.map((obra) => ({
    clave: `guardada-${obra.id}`,
    tipo: obra.tipo,
    capacidad_descripcion: obra.capacidad_descripcion ?? "",
    validada: obra.validada,
    id: obra.id,
    terreno_ref: obra.terreno_ref ?? null,
    terreno_ref_guardado: obra.terreno_ref ?? null,
  }));
}

/**
 * El cuerpo que viaja al servidor.
 *
 * SE MANDA TODO, incluso lo que el tipo no admite: el servidor lo descarta
 * —{@see InformeAguaFuente::limpiarSegunElTipo()}— y esa es a propósito la
 * única defensa. Filtrar también acá pondría la misma regla en dos lugares, y
 * el día que entre un tercer tipo de fuente habría que acordarse de los dos.
 *
 * Los tres números pasan por `numeroEscrito` y no por `Number`: acá los miles
 * van con punto y los decimales con coma, así que `Number("1.234")` daría
 * 1,234 litros por segundo donde la persona escribió mil doscientos treinta y
 * cuatro.
 */
export function inputDesdeElFormulario(
  noAplica: boolean,
  fuentes: FilaDeFuenteDeAgua[],
  obras: FilaDeObraDeAgua[],
  notas: string,
  descripcion = "",
  descripcionValidada = false,
): GuardarAguasInput {
  const descripcionLimpia = vacioComoNulo(descripcion);

  return {
    no_aplica: noAplica,
    // Vaciar el texto BORRA la descripción, y sin texto no hay nada que haya
    // revisado una persona: la casilla viaja apagada.
    descripcion_general: descripcionLimpia,
    descripcion_general_validada: descripcionLimpia === null ? false : descripcionValidada,
    fuentes: fuentes.map((fila) => ({
      ...identidadParaElServidor(fila),
      tipo: fila.tipo,
      rio_canal: vacioComoNulo(fila.rio_canal),
      acciones: numeroEscrito(fila.acciones),
      litros_seg: numeroEscrito(fila.litros_seg),
      equivalencia_ls_accion: numeroEscrito(fila.equivalencia_ls_accion),
      estado_inscripcion: fila.estado_inscripcion === "" ? null : fila.estado_inscripcion,
      validada: fila.validada,
    })),
    obras: obras.map((fila) => ({
      ...identidadParaElServidor(fila),
      tipo: fila.tipo,
      capacidad_descripcion: vacioComoNulo(fila.capacidad_descripcion),
      validada: fila.validada,
    })),
    notas_observaciones: vacioComoNulo(notas),
  };
}

/**
 * El `id` (si la fila ya existe) y el enlace con terreno (solo si cambió).
 *
 * UNA FILA NUEVA manda el enlace cuando lo tiene; una guardada, solo cuando
 * difiere de lo que el servidor tenía. `null` explícito lo quita; la clave
 * ausente lo conserva.
 */
function identidadParaElServidor(fila: IdentidadDeLaFila): { id?: number; terreno_ref?: string | null } {
  const identidad: { id?: number; terreno_ref?: string | null } = {};

  if (fila.id !== null) identidad.id = fila.id;
  if (fila.terreno_ref !== fila.terreno_ref_guardado) identidad.terreno_ref = fila.terreno_ref;

  return identidad;
}

function vacioComoNulo(valor: string): string | null {
  const limpio = valor.trim();

  return limpio === "" ? null : limpio;
}

/* --- El total de agua disponible ------------------------------------------ */

/**
 * La suma de los caudales de TODAS las fuentes, en litros por segundo.
 *
 * SUMA EN CENTÉSIMAS ENTERAS y divide al final, el mismo algoritmo que
 * `sumarSuperficies`. No es prolijidad: la cifra se imprime justo debajo de
 * los números que la persona acaba de escribir, y en punto flotante
 * `38,5 + 20,1` da 58,599999999999994 — un total que no cuadra con la suma que
 * cualquiera hace de cabeza mirando la misma pantalla.
 *
 * SUMA LAS DOS CLASES DE FUENTE, y eso es el punto: `litros_seg` es la única
 * columna que canales y pozos comparten, justamente porque es la que se suma.
 * Las OBRAS no entran —un tranque guarda agua, no la aporta—.
 *
 * `null` Y NO `0` cuando ninguna fila trae caudal: un cuadro sin ningún dato
 * no sumó cero, no tiene nada que sumar. Es el mismo criterio con el que el
 * total de superficies imprime "—", y la pantalla lo imprime igual.
 *
 * PARCIAL cuando algunas filas traen caudal y otras no: se suman las que hay,
 * sin fingir que los vacíos son cero.
 */
export function totalDeAguaDisponible(fuentes: FilaDeFuenteDeAgua[]): number | null {
  let centesimas = 0;
  let huboAlguno = false;

  for (const fila of fuentes) {
    const caudal = numeroEscrito(fila.litros_seg);

    if (caudal === null || !Number.isFinite(caudal)) continue;

    centesimas += Math.round(caudal * 100);
    huboAlguno = true;
  }

  return huboAlguno ? centesimas / 100 : null;
}

/**
 * El total ya escrito, con la coma decimal y sin ceros de relleno.
 *
 * SIN SEPARADOR DE MILES a propósito: un caudal de cuatro cifras en l/s no
 * existe en un predio —el Aconcagua entero no llega—, así que el punto solo
 * agregaría ruido sobre una cifra que siempre tiene tres dígitos o menos.
 */
export function totalEscrito(total: number | null): string {
  if (total === null) return "—";

  return `${total.toString().replace(".", ",")} l/s`;
}

export function cuantasFuentes(cantidad: number): string {
  return cantidad === 1 ? "fuente de agua" : "fuentes de agua";
}

/** Lo que `contarCambiosDeAguas` compara: el borrador de la pestaña. */
interface CambiosDeAguas {
  noAplica: boolean;
  notas: string;
  descripcion: string;
  descripcionValidada: boolean;
  fuentes: FilaDeFuenteDeAgua[];
  obras: FilaDeObraDeAgua[];
}

/**
 * Cuántas cosas de la pestaña difieren de lo último guardado: la casilla de
 * "sin agua", la descripción, su casilla y las notas cuentan una cada una, y cada fila editada, agregada o
 * quitada cuenta una.
 *
 * Las filas se comparan por posición y por contenido, NUNCA por su `clave`: es
 * sintética —un uuid para las nuevas— así que dos borradores idénticos para el
 * usuario tienen claves distintas, y comparar el JSON entero dejaría el aviso de
 * "cambios sin guardar" encendido después de agregar y quitar una fila.
 */
export function contarCambiosDeAguas(
  actual: CambiosDeAguas,
  base: CambiosDeAguas,
): number {
  let cambios = 0;

  if (actual.noAplica !== base.noAplica) cambios++;
  if (actual.notas !== base.notas) cambios++;
  if (actual.descripcion !== base.descripcion) cambios++;
  if (actual.descripcionValidada !== base.descripcionValidada) cambios++;

  for (let i = 0; i < Math.max(actual.fuentes.length, base.fuentes.length); i++) {
    const a = actual.fuentes[i];
    const b = base.fuentes[i];

    if (
      a === undefined ||
      b === undefined ||
      a.tipo !== b.tipo ||
      a.rio_canal !== b.rio_canal ||
      a.acciones !== b.acciones ||
      a.litros_seg !== b.litros_seg ||
      a.equivalencia_ls_accion !== b.equivalencia_ls_accion ||
      a.estado_inscripcion !== b.estado_inscripcion ||
      a.terreno_ref !== b.terreno_ref ||
      a.validada !== b.validada
    ) {
      cambios++;
    }
  }

  for (let i = 0; i < Math.max(actual.obras.length, base.obras.length); i++) {
    const a = actual.obras[i];
    const b = base.obras[i];

    if (
      a === undefined ||
      b === undefined ||
      a.tipo !== b.tipo ||
      a.capacidad_descripcion !== b.capacidad_descripcion ||
      a.terreno_ref !== b.terreno_ref ||
      a.validada !== b.validada
    ) {
      cambios++;
    }
  }

  return cambios;
}
